import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './CoursesMain.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Button, Pagination, CircularProgress, Typography } from '@mui/material';  // Added Pagination from MUI
import { Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from '../axios.config';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { useParams } from 'react-router-dom';


const CourseReport = () => {
  const lngsltd = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('languageSelected')) || {};
    } catch (error) {
      console.error('Error parsing language data:', error);
      return {};
    }
  }, []);

  const [progressData, setProgressData] = useState([]);
  const [storeLocations, setStoreLocations] = useState({});
  const [filterText, setFilterText] = useState('');
  const [storeOptions, setStoreOptions] = useState([]);
  const [courseName, setCourseName] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [coursesPerPage] = useState(10);
  const [dynamicText, setDynamicText] = useState(lngsltd["Loading...Please wait"] || "Loading...Please wait");

  const { id } = useParams();

  const fetchStoreData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/Store`);
      const storeLocationMap = {};
      const options = response.data.map(store => ({
        id: store.storeID,
        name: store.storeLocation
      }));
      response.data.forEach(store => {
        storeLocationMap[store.storeID] = store.storeLocation;
      });
      setStoreLocations(storeLocationMap);
      setStoreOptions(options);
    } catch (error) {
      // console.error('Error fetching store locations', error);
    }
  }, []);

  const fetchCourseSteps = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/UserCourseStep/GetUser&CourseSteps/${id}`);
      setProgressData(response.data || []);
      if (response.data && response.data.length > 0) {
        const firstRecord = response.data[0];
        setCourseName(firstRecord?.courseName || '');
      } else {
        setCourseName('');
        setTimeout(() => {
          setDynamicText(lngsltd["No Data Found"] || "No Data Found");
        }, 2000);
      }
    } catch (error) {
      toast.info(lngsltd['There was an issue to get the data'] || 'There was an issue to get the data');
      setProgressData([]);
      setCourseName('');
    }
  }, [id, lngsltd]);

  useEffect(() => {
    fetchStoreData();
    fetchCourseSteps();
  }, [id, fetchStoreData, fetchCourseSteps]);

  const handleClearFilter = () => {
    setFilterText('');
  };

  const [filteredProgressData, setFilteredProgressData] = useState([]);

  useEffect(() => {
    if (progressData.length !== 0) {
      const filteredData = progressData.filter(progress => {
        const storeLocation = storeLocations[progress.storeID] || '';
        return (
          progress.userName?.toLowerCase().includes(filterText.toLowerCase()) ||
          storeLocation.toLowerCase().includes(filterText.toLowerCase()) ||
          progress.progressPercentage?.toString().toLowerCase().includes(filterText.toLowerCase()) ||
          progress.status?.toLowerCase().includes(filterText.toLowerCase()) ||
          String(progress.quizScore).toLowerCase().includes(filterText.toLowerCase())
        );
      });
      setFilteredProgressData(filteredData);
      setCurrentPage(1);
      if (filteredData.length === 0) {
        setDynamicText("No Data Found");
      } else {
        setDynamicText("Loading...Please wait");
      }
    } else {
      setFilteredProgressData([]);
    }
  }, [filterText, progressData, storeLocations]);

  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = filteredProgressData.slice(indexOfFirstCourse, indexOfLastCourse);

  const paginate = (event, pageNumber) => setCurrentPage(pageNumber);

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'status-badge status-completed';
      case 'in progress':
        return 'status-badge status-in-progress';
      case 'not started':
        return 'status-badge status-not-started';
      default:
        return 'status-badge';
    }
  };

  const handleExportExcel = () => {
    if (!filteredProgressData || filteredProgressData.length === 0) {
      toast.info("No Data Found");
      return;
    }

    const exportData = filteredProgressData.map(item => ({
      UserName: item.userName,
      Progress: `${item.progressPercentage}%`,
      "Store Location": storeLocations[item.storeID] || 'N/A',
      Status: item.status,
      "Quiz Score": item.quizScore
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const range = XLSX.utils.decode_range(worksheet['!ref']);

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 14 },
      fill: { fgColor: { rgb: "2F75B5" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" }
      }
    };

    const dataCellStyle = {
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }
    };

    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      worksheet[cellAddress].s = headerStyle;
    }

    for (let row = 1; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (worksheet[cellAddress]) {
          worksheet[cellAddress].s = dataCellStyle;
        }
      }
    }

    worksheet['!cols'] = Object.keys(exportData[0]).map(key => {
      const maxLength = Math.max(
        key.length,
        ...exportData.map(row => String(row[key] ?? '').length)
      );
      return { wch: maxLength + 4 };
    });

    worksheet['!rows'] = [
      { hpt: 28 },
      ...Array(range.e.r).fill({ hpt: 22 })
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Course Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true
    });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

    saveAs(blob, `${courseName || "Course_Report"}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="reportpageContainer reportdiv w-100 d-flex flex-column bg-light m-3">
      <h1 className="reportpageTitle text-center">
        {courseName ? `${String(courseName).toUpperCase()} - ${lngsltd["Course Report"]?.toUpperCase() || "COURSE REPORT"}` : lngsltd["Course Report"]?.toUpperCase() || "COURSE REPORT"}
      </h1>

      <div className="report-content-wrapper">
        <div className="report-filters-section">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center gap-2" style={{ width: '50%' }}>
              <input
                type="text"
                className="form-control flex-grow-1"
                placeholder={lngsltd["Search"]}
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
              <Button
                variant="outlined"
                color="secondary"
                style={{ whiteSpace: 'nowrap' }}
                onClick={handleClearFilter}
              >
                {lngsltd["Clear Filter"] || "Clear Filter"}
              </Button>
            </div>
            <Button
              variant="contained"
              color="success"
              style={{ whiteSpace: 'nowrap' }}
              onClick={handleExportExcel}
            >
              {lngsltd["Export Report"] || "Export Report"}
            </Button>
          </div>
        </div>

        <div className="table-wrapper">
          <Table striped bordered hover className="report-table-grid">
            <thead>
              <tr>
                <th className="text-center">{lngsltd["UserName"]}</th>
                <th className="text-center">{lngsltd["Progress"]}</th>
                <th className="text-center">{lngsltd["Store Location"]}</th>
                <th className="text-center">{lngsltd["Status"]}</th>
                <th className="text-center">Quiz Score</th>
              </tr>
            </thead>
            <tbody>
              {currentCourses && currentCourses.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    {dynamicText}
                  </td>
                </tr>
              ) : (
                currentCourses.map((progress, index) => (
                  <tr key={index}>
                    <td className="text-center">{progress.userName}</td>
                    <td className="text-center">
                      <div className="progress-display">
                        <div className="progress-text">{progress.progressPercentage}%</div>
                        <div className="progress-bar-wrapper">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${progress.progressPercentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="text-center">{storeLocations[progress.storeID] || 'N/A'}</td>
                    <td className="text-center">
                      <span className={getStatusBadgeClass(progress.status)}>
                        {progress.status}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className="quiz-badge">{progress.quizScore}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {filteredProgressData.length > coursesPerPage && (
          <div className="pagination-section">
            <Pagination
              count={Math.ceil(filteredProgressData.length / coursesPerPage)}
              page={currentPage}
              onChange={paginate}
              siblingCount={1}
              boundaryCount={1}
              color="primary"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseReport;
